import { TIME_SCOPE_LABELS } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Page } from '#client/components/ui/Page';
import { Segmented } from '#client/components/ui/Segmented';
import { StatTile } from '#client/components/ui/StatTile';
import { useDelayedFlag } from '#client/hooks/useDelayedFlag';
import { useAuth } from '#client/providers/AuthProvider';
import {
  AttentionCard,
  DashboardSkeleton,
  LabPulseCard,
  QueueCard,
  QuickActionsCard,
  SnapshotCard,
} from './DashboardCards';
import { formatLongDate, greetingFor } from './dashboard.logic';
import { TopTestsCard } from './TopTestsCard';
import { useDashboard } from './useDashboard';
import { SCOPES, useDashboardScope } from './useDashboardScope';

const SCOPE_OPTIONS = SCOPES.map((value) => ({ value, label: TIME_SCOPE_LABELS[value] }));

/**
 * The lab's front door.
 *
 * It answers one question — "what is waiting on me?" — which is what every
 * comparable diagnostic-lab product opens on. It is deliberately not the
 * registration form: that is a data-entry screen a technician is forbidden to
 * submit, and this is the URL the logo and a stale tab both resolve to.
 *
 * Everything here drills through to the worklist, pre-filtered. A dashboard
 * that only displays numbers is a poster.
 */
export function DashboardPage() {
  const { user } = useAuth();
  const [scope, setScope] = useDashboardScope(user?.id);

  const { data, loading, error } = useDashboard(scope);
  const showSkeleton = useDelayedFlag(loading, 250);
  const now = new Date();

  return (
    <Page
      title={`${greetingFor(now.getHours())}, ${user?.first_name ?? ''}`.trim()}
      subtitle={formatLongDate(now)}
      fullWidth
      primaryAction={
        <Segmented label="Time scope" options={SCOPE_OPTIONS} value={scope} onChange={setScope} />
      }
    >
      {error && <Banner tone="critical">{error}</Banner>}

      {!data && showSkeleton && <DashboardSkeleton />}

      {data && (
        <>
          <div className="c-dashboard__kpis">
            {data.kpis.map((tile) => (
              <StatTile key={tile.key} tile={tile} />
            ))}
          </div>

          <div className="grid gap-5 xl:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
            <QueueCard queue={data.my_queue} />
            <AttentionCard attention={data.attention} />
          </div>

          <QuickActionsCard />

          {/*
            Adaptive rendering: with no test volume in scope there is nothing to
            chart, and an empty chart card is worse than no card.

            The ranking below is *not* gated the same way. It fetches its own
            data against its own mode, so gating it on a figure from a different
            request would hide it for the wrong reason.
          */}
          {data.lab_pulse.top_tests.length > 0 && <LabPulseCard pulse={data.lab_pulse} />}

          <TopTestsCard scope={scope} />

          <SnapshotCard />
        </>
      )}
    </Page>
  );
}
